import React from 'react';
import './TradingCard.css';

// Item art comes from the Solarflare icon atlas: 32px cells, drawn at 3x
const ATLAS = { url: '/img/cards/solarflare-items.png', width: 816, height: 578, scale: 3 };
const TYPE_LABEL = { character: 'Character', world: 'World', item: 'Item' };
const RARITY_LABEL = { common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary' };

// Small seeded random so each card's starfield is the same every time
function seeded(text) {
    let h = 2166136261;
    for (let i = 0; i < text.length; i++) {
        h = Math.imul(h ^ text.charCodeAt(i), 16777619);
    }
    return () => {
        h = Math.imul(h ^ (h >>> 15), 2246822507);
        h = Math.imul(h ^ (h >>> 13), 3266489909);
        return ((h ^= h >>> 16) >>> 0) / 4294967296;
    };
}

function Stars({ id, count = 26 }) {
    let random = seeded(id);
    return Array.from({ length: count }, (_, i) => (
        <circle key={i} cx={random() * 200} cy={random() * 140} r={random() < 0.85 ? 0.7 : 1.3} fill="#fff" opacity={0.25 + random() * 0.6} />
    ));
}

// A character: their rendered portrait if they have one, otherwise a silhouette in their colours with glowing eyes,
// over a glow in their colour
function FigureArt({ card }) {
    let a = card.art;
    let shoulders = { slim: 46, standard: 54, heavy: 64 }[a.build] || 54;
    let id = `fig-${card.id}`;
    return (
        <svg viewBox="0 0 200 140" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            <defs>
                <radialGradient id={`${id}-glow`} cx="50%" cy="45%" r="60%">
                    <stop offset="0" stopColor={a.glow} stopOpacity="0.55" />
                    <stop offset="1" stopColor={a.glow} stopOpacity="0" />
                </radialGradient>
                {a.portraitBg ?
                    <linearGradient id={`${id}-portrait-bg`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor={a.portraitBg[0]} />
                        <stop offset="1" stopColor={a.portraitBg[1]} />
                    </linearGradient>
                    : null}
                <linearGradient id={`${id}-body`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor={a.primary} />
                    <stop offset="1" stopColor={a.secondary} />
                </linearGradient>
            </defs>
            {a.portraitBg ?
                // an illustrated portrait: square, on its own background, which carries on either side of it
                <>
                    <rect width="200" height="140" fill={`url(#${id}-portrait-bg)`} />
                    <image href={a.portrait} x="0" y="0" width="200" height="140" preserveAspectRatio="xMidYMid meet" />
                </>
                :
                <>
                    <rect width="200" height="140" fill={a.secondary} />
                    <rect width="200" height="140" fill={`url(#${id}-glow)`} />
                    {a.portrait ?
                        <image href={a.portrait} x="0" y="0" width="200" height="140" preserveAspectRatio="xMidYMid slice" />
                        :
                        <Silhouette a={a} id={id} shoulders={shoulders} />
                    }
                </>
            }
        </svg>
    );
}

// The drawn bust for characters without a rendered portrait
function Silhouette({ a, id, shoulders }) {
    return (
        <>
            <path d={`M ${100 - shoulders} 140 C ${100 - shoulders} 104 ${100 - shoulders * 0.5} 92 100 92 C ${100 + shoulders * 0.5} 92 ${100 + shoulders} 104 ${100 + shoulders} 140 Z`} fill={`url(#${id}-body)`} />
            <rect x="90" y="78" width="20" height="18" fill={a.secondary} />
            {a.robot ?
                <rect x="70" y="34" width="60" height="50" rx="14" fill={`url(#${id}-body)`} />
                :
                <ellipse cx="100" cy="58" rx={a.build === 'heavy' ? 27 : 24} ry="28" fill={`url(#${id}-body)`} />
            }
            {a.robot ? <path d="M 100 34 L 100 20 M 94 18 L 106 18" stroke={a.glow} strokeWidth="3" strokeLinecap="round" /> : null}
            {a.crown ? <path d="M 76 36 L 82 14 L 90 30 L 100 8 L 110 30 L 118 14 L 124 36" fill="none" stroke={a.glow} strokeWidth="3" strokeLinejoin="round" /> : null}
            {a.visor ?
                <rect x="78" y="50" width="44" height="10" rx="5" fill={a.glow} opacity="0.9" />
                :
                <g fill={a.glow}>
                    <ellipse cx="91" cy="56" rx="4" ry="2.6" />
                    <ellipse cx="109" cy="56" rx="4" ry="2.6" />
                </g>
            }
        </>
    );
}

// A world: a planet against stars, or a glowing portal for realms
function PlanetArt({ card }) {
    let a = card.art;
    let id = `pl-${card.id}`;
    return (
        <svg viewBox="0 0 200 140" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            <defs>
                <radialGradient id={`${id}-sun`} cx="88%" cy="12%" r={a.bigSun ? '70%' : '45%'}>
                    <stop offset="0" stopColor={a.sun} stopOpacity="0.9" />
                    <stop offset="1" stopColor={a.sun} stopOpacity="0" />
                </radialGradient>
                <radialGradient id={`${id}-surface`} cx="65%" cy="30%" r="80%">
                    <stop offset="0" stopColor={a.surface} />
                    <stop offset="0.7" stopColor={a.surface} stopOpacity="0.75" />
                    <stop offset="1" stopColor="#050508" />
                </radialGradient>
            </defs>
            <rect width="200" height="140" fill="#07070b" />
            <Stars id={card.id} />
            <rect width="200" height="140" fill={`url(#${id}-sun)`} />
            {a.realm ?
                <g fill="none">
                    <ellipse cx="100" cy="74" rx="52" ry="52" stroke={a.sky} strokeWidth="10" opacity="0.5" />
                    <ellipse cx="100" cy="74" rx="40" ry="40" fill={a.surface} stroke={a.sun} strokeWidth="3" />
                    <ellipse cx="100" cy="74" rx="26" ry="26" stroke={a.sun} strokeWidth="2" opacity="0.6" />
                    <ellipse cx="100" cy="74" rx="12" ry="12" stroke={a.sun} strokeWidth="1.5" opacity="0.4" />
                </g>
                :
                <g>
                    <circle cx="100" cy="76" r="50" fill={a.sky} opacity="0.25" />
                    <circle cx="100" cy="76" r="46" fill={`url(#${id}-surface)`} />
                    {a.islands ?
                        <g fill="#4a8a3a" opacity="0.85">
                            <ellipse cx="86" cy="62" rx="9" ry="5" />
                            <ellipse cx="114" cy="84" rx="7" ry="4" />
                            <ellipse cx="96" cy="96" rx="5" ry="3" />
                        </g>
                        : null}
                    {a.clouds ?
                        <g stroke="#c8d0b8" strokeWidth="5" strokeLinecap="round" opacity="0.35">
                            <path d="M 66 64 L 118 64" />
                            <path d="M 80 78 L 140 78" />
                            <path d="M 62 92 L 108 92" />
                        </g>
                        : null}
                    <circle cx="100" cy="76" r="46" fill="none" stroke={a.sky} strokeWidth="2" opacity="0.8" />
                </g>
            }
        </svg>
    );
}

function IconArt({ card }) {
    let style = {
        backgroundImage: `url(${ATLAS.url})`,
        backgroundSize: `${ATLAS.width * ATLAS.scale}px ${ATLAS.height * ATLAS.scale}px`,
        backgroundPosition: `-${card.art.x * ATLAS.scale}px -${card.art.y * ATLAS.scale}px`
    };
    return (
        <div className="tc-icon-art">
            <div className="tc-icon" style={style} role="img" aria-label={card.name}></div>
        </div>
    );
}

// A picture: a pixel-art sprite shown whole on a gradient, or a photo that fills the art window
function ImageArt({ card }) {
    let a = card.art;
    if (a.photo) {
        return <img className="tc-photo-art" src={a.photo} alt="" />;
    }
    let bg = a.bg || ['#161616', '#2a2a2a'];
    return (
        <div className="tc-sprite-art" style={{ backgroundImage: `linear-gradient(${bg[0]}, ${bg[1]})` }}>
            <img src={a.sprite} alt="" />
        </div>
    );
}

function CardArt({ card }) {
    if (card.art.kind === 'figure') {
        return <FigureArt card={card} />;
    }
    if (card.art.kind === 'planet') {
        return <PlanetArt card={card} />;
    }
    if (card.art.kind === 'image') {
        return <ImageArt card={card} />;
    }
    return <IconArt card={card} />;
}

// Each card set's logo: on its pack, and on the backs of the cards dealt from it
export const SET_LOGO = {
    solarflare: '/img/cards/solarflare-logo.png',
    spacejump: '/img/cards/spacejump/spacejump-logo.png'
};

export function CardBack({ set }) {
    let id = SET_LOGO[set] ? set : 'solarflare';
    return (
        <div className={`tc tc-back tc-back-${id}`} aria-label="Face-down card">
            <img src={SET_LOGO[id]} alt="" />
        </div>
    );
}

// One trading card. `locked` shows an uncollected card as a numbered silhouette; `detailed` adds the card's text;
// `trimmed` draws the gold-trimmed version; `isNewTrim` marks a first Trimmed copy of a card already owned.
export function TradingCard({ card, total, count, isNew, isNewTrim, trimmed, locked, detailed, onClick }) {
    let own = `#${String(card.number).padStart(3, '0')}`;
    let ofTotal = total ? ` / ${String(total).padStart(3, '0')}` : '';
    let number = own + ofTotal;
    // the " / 097" part is hidden on narrow cards (TradingCard.css) so the footer stays on one line
    let numberLabel = <>{own}{ofTotal ? <span className="tc-total">{ofTotal}</span> : null}</>;
    let Tag = onClick ? 'button' : 'div';

    if (locked) {
        return (
            <Tag type={onClick ? 'button' : undefined} className="tc tc-locked" onClick={onClick} aria-label={`Card ${number}, not collected yet`}>
                <span className="tc-locked-mark">?</span>
                <span className="tc-number">{numberLabel}</span>
            </Tag>
        );
    }

    return (
        <Tag type={onClick ? 'button' : undefined} className={`tc tc-${card.rarity}${trimmed ? ' tc-trimmed' : ''}${detailed ? ' tc-detailed' : ''}`} onClick={onClick} aria-label={onClick ? `${card.name}, ${trimmed ? 'Trimmed ' : ''}${RARITY_LABEL[card.rarity]} ${TYPE_LABEL[card.type]}` : undefined}>
            <span className="tc-header">
                <span className="tc-name">{card.name}</span>
                <span className="tc-type">{TYPE_LABEL[card.type]}</span>
            </span>
            <span className="tc-art">
                <CardArt card={card} />
                {isNew ? <span className="tc-new">New</span> : isNewTrim ? <span className="tc-new tc-new-trim">New trim</span> : null}
                {count > 1 ? <span className="tc-count">×{count}</span> : null}
            </span>
            <span className="tc-subtitle">{card.subtitle}</span>
            {detailed ? <span className="tc-text">{card.text}</span> : null}
            <span className="tc-footer">
                <span className="tc-rarity">
                    {RARITY_LABEL[card.rarity]}
                    {/* the gold frame already says Trimmed: small cards add a ✦, the enlarged card spells it out */}
                    {trimmed ?
                        detailed ?
                            <span className="tc-trim-label"> · Trimmed</span>
                            :
                            <span className="tc-trim-label" title="Trimmed"> ✦<span className="sr-only">Trimmed</span></span>
                        : null}
                </span>
                <span className="tc-number">{numberLabel}</span>
            </span>
        </Tag>
    );
}
